import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  darken,
  Grid,
  IconButton,
  InputAdornment,
  lighten,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';

import { DateTime } from 'luxon';

import { useTranslation } from 'react-i18next';
import {
  useGetPowerLoadProfileAnalysisQuery,
  useGetPowerLoadProfileAnalysisSeuQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import { showSnackbar } from '../../../../redux/snackbarSlice';
import PowerLoadProfilesAnalysisChart from '@/components/highcharts/powerAnalyser/PowerLoadProfilesAnalysisChart';
import EnergyTreeNodeAutocomplete from '@/features/energyxplorer/components/EnergyTreeNodeAutocomplete';
import TimestampSummaryWarningIcon from '@/features/energyxplorer/components/TimestampsSummaryWarningIcon';
import TimestampSummarySeuWarningIcon from '@/features/energyxplorer/components/TimestampsSummarySeuWarningIcon';

const HeaderDisplay = ({ label, description }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        mb: 2,
      }}
    >
      <Typography variant="body1" color="textSecondary" fontWeight="bold">
        {label}
      </Typography>
      <Tooltip title={description} placement="right">
        <InfoRoundedIcon
          fontSize="small"
          sx={{
            color: (theme) =>
              theme.palette.mode === 'light' ? 'lightgray' : 'gray',
            ml: 1,
          }}
        />
      </Tooltip>
    </Box>
  );
};

const ValueDisplay = ({
  label = t('common.mean'),
  value = 0,
  unit = 'kWh',
  trend = 0,
}) => {
  const trendLabel = `${trend > 0 ? '+' : ''}${trend.toFixed(0)}%`;
  const trendColor = (() => {
    if (trend >= 10) return 'error';
    if (trend >= 2) return 'warning';
    if (trend >= -2) return 'primary';
    return 'success';
  })();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Box
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
        >
          <Typography variant="h4" color="textPrimary" fontWeight={'bold'}>
            {value} {unit}
          </Typography>
          <Chip
            sx={{
              marginLeft: 1,
              fontWeight: 'bold',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? lighten(theme.palette[trendColor].main, 0.9)
                  : darken(theme.palette[trendColor].main, 0.8),
              color: (theme) => theme.palette[trendColor].dark,
              border: (theme) => `1px solid ${theme.palette[trendColor].light}`,
            }}
            label={trendLabel}
            size={'small'}
          />
        </Box>
      </Box>
    </Box>
  );
};

// MAKE CHIP CARD AND MAKE IT LIKE INITIATIVES TAB

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const PowerLoadProfilesAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  timeRange,
  setTimestampSummary,
  setTimestampSummarySetBy,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  useEffect(() => {
    setSelectedRange([
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ]);
  }, [timeRange]);

  const [nodeToAnalyse, setNodeToAnalyse] = useState(null);
  const [seuToAnalyse, setSeuToAnalyse] = useState(null);
  const [analysisMode, setAnalysisMode] = useState('node'); // or 'seu'
  const [filtersOpen, setFiltersOpen] = useState(true);

  const {
    data: nodeAnalysisData,
    isFetching: nodeAnalysisDataFetching,
    isError: nodeAnalysisError,
  } = useGetPowerLoadProfileAnalysisQuery(
    {
      accountId: selectedAccount.AccountId,
      energyGroupId: selectedEnergyGroup.energyGroupId,
      nodeId: nodeToAnalyse?.energyTreeNodeId,
      startTimestamp: timeRange.start,
      endTimestamp: timeRange.end,
    },
    { skip: !nodeToAnalyse || !selectedEnergyGroup || !selectedAccount },
  );
  const {
    data: seuAnalysisData,
    isFetching: seuAnalysisDataFetching,
    isError: seuAnalysisError,
  } = useGetPowerLoadProfileAnalysisSeuQuery(
    {
      accountId: selectedAccount.AccountId,
      energyGroupId: selectedEnergyGroup.energyGroupId,
      seuType: seuToAnalyse,
      startTimestamp: timeRange.start,
      endTimestamp: timeRange.end,
    },
    { skip: !seuToAnalyse || !selectedEnergyGroup || !selectedAccount },
  );

  useEffect(() => {
    if (!nodeToAnalyse && selectedEnergyGroup?.energyTreeNodes.length > 0) {
      setNodeToAnalyse(selectedEnergyGroup?.energyTreeNodes[0]);
    }
  }, [nodeToAnalyse, selectedEnergyGroup]);

  useEffect(() => {
    if (nodeAnalysisError || seuAnalysisError)
      dispatch(
        showSnackbar({
          severity: 'error',
          message: 'Der skete en fejl i udregningen af analysen',
        }),
      );
  }, [dispatch, nodeAnalysisError, seuAnalysisError]);

  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();

    // First pass: add all nodes
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      map.set(node.energyTreeNodeId, { ...node, children: [] });
    });

    // Second pass: link children to parents
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      if (node.parentId && map.has(node.parentId)) {
        map.get(node.parentId).children.push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [],
    [nodeToAnalyse, nodeLookup],
  );
  const parent = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.parentId) || null,
    [nodeToAnalyse, nodeLookup],
  );

  const seuInputRef = useRef(null);

  const validSeuOptions = useMemo(() => {
    return (
      Array.from(nodeGroupsByType.entries())
        // eslint-disable-next-line no-unused-vars
        .filter(([type, nodes]) =>
          nodes.some((node) => node.dataStreams?.length > 0),
        )
        .map(([type]) => type)
        .filter(
          (type) =>
            type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
        )
    );
  }, [nodeGroupsByType]);

  const effectiveSeuToAnalyse = seuToAnalyse ?? validSeuOptions[0];

  useEffect(() => {
    if (analysisMode === 'seu' && seuInputRef.current) {
      // blur the input immediately after mounting so dropdown doesn't pop open
      seuInputRef.current.blur();
      setSeuToAnalyse(effectiveSeuToAnalyse);
    }
  }, [analysisMode, effectiveSeuToAnalyse]);

  useEffect(() => {
    if (nodeAnalysisData) {
      setTimestampSummary(nodeAnalysisData?.TimestampsSummary);
      setTimestampSummarySetBy('Belastningsanalysen');
    } else if (seuAnalysisData) {
      setTimestampSummary(seuAnalysisData?.TimestampsSummary);
      setTimestampSummarySetBy('Belastningsanalysen');
    } else {
      setTimestampSummary(null);
      setTimestampSummarySetBy(null);
    }
  }, [
    nodeAnalysisData,
    setTimestampSummary,
    setTimestampSummarySetBy,
    seuAnalysisData,
  ]);

  const nodeData = nodeAnalysisData?.PowerLoadProfileData;
  const seuData = seuAnalysisData?.PowerLoadProfileData;

  const nodePowerData = nodeAnalysisData?.PowerData;
  const seuPowerData = seuAnalysisData?.PowerData;

  const loadData = analysisMode === 'node' ? nodeData : seuData;
  const powerData = analysisMode === 'node' ? nodePowerData : seuPowerData;

  const nodeSummary = nodeAnalysisData?.PowerLoadProfileSummary;
  const seuSummary = seuAnalysisData?.PowerLoadProfileSummary;

  const summaryData = analysisMode === 'node' ? nodeSummary : seuSummary;

  const analysisError =
    analysisMode === 'node' ? nodeAnalysisError : seuAnalysisError;
  return (
    <Box>
      <Grid container spacing={2}>
        <Cards data={summaryData} />
        <Grid
          item
          md={12}
          lg={filtersOpen ? 9 : 12}
          sx={{ transition: 'flex-basis 0.3s ease' }}
        >
          <Card variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <CardContent sx={{ minHeight: 400 }}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {true && (
                        <>
                          <ListItemText
                            primary={analysisMode === 'node' ? `Node` : `SEU`}
                            primaryTypographyProps={{
                              color: 'text.secondary',
                              variant: 'body2',
                            }}
                            secondary={
                              analysisMode === 'node'
                                ? `${nodeToAnalyse?.name}`
                                : `${seuToAnalyse}`
                            }
                            secondaryTypographyProps={{
                              color: 'text.primary',
                              variant: 'h6',
                              fontWeight: 'bold',
                            }}
                          />
                          {analysisMode === 'node' ? (
                            <TimestampSummaryWarningIcon node={nodeToAnalyse} />
                          ) : (
                            <TimestampSummarySeuWarningIcon
                              seu={seuToAnalyse}
                              energyGroupId={selectedEnergyGroup.energyGroupId}
                            />
                          )}
                          <Tooltip
                            title="Belastningsanalysen viser, hvornår forbruget har ligget på et givent kW-niveau i den valgte periode. Analysen giver blandt andet indsigt i grundbelastning og spidsbelastninger."
                            placement="right"
                          >
                            <InfoRoundedIcon
                              fontSize="small"
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'lightgray'
                                    : 'gray',
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('power_load_profiles.in_period')}{' '}
                      {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : t('power_load_profiles.select_period')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: 'flex', alignItems: 'center' }}
                    className="non-printable"
                  >
                    <Tooltip
                      title={`${filtersOpen ? t('common.hide') : t('common.show')}`}
                      placement="top"
                    >
                      <IconButton
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          ml: 4,
                        }}
                      >
                        {filtersOpen ? (
                          <KeyboardArrowRightRoundedIcon />
                        ) : (
                          <KeyboardArrowLeftRoundedIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Stack>
              {!analysisError ? (
                <Suspense fallback={<div>Loading...</div>}>
                  <PowerLoadProfilesAnalysisChart
                    loadData={loadData}
                    powerData={powerData}
                    isLoading={
                      nodeAnalysisDataFetching || seuAnalysisDataFetching
                    }
                  />
                </Suspense>
              ) : (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>Ingen data tilgængelig</AlertTitle>
                  Det er ikke muligt at vise analysen.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen && (
          <Grid className="non-printable" item md={12} lg={3}>
            <Grid item xs sx={{ mt: 0 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid item xs>
                      {analysisMode === 'node' ? (
                        <>
                          <EnergyTreeNodeAutocomplete
                            nodeOptions={selectedEnergyGroup?.energyTreeNodes}
                            selectedNode={nodeToAnalyse}
                            onChange={(newValue) => {
                              if (newValue) setNodeToAnalyse(newValue);
                            }}
                          />
                          <Box
                            mt={1}
                            display="flex"
                            justifyContent="flex-start"
                          >
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('seu');
                              }}
                            >
                              {t('buttonLabel.select_seu')}
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <>
                          <Autocomplete
                            options={validSeuOptions} // SEU types
                            getOptionLabel={(option) => option}
                            value={seuToAnalyse}
                            onChange={(e, newValue) =>
                              setSeuToAnalyse(newValue)
                            }
                            disableClearable
                            renderOption={(props, option) => {
                              const isDisabled = [
                                'Hovedmåler',
                                'Tavle',
                                'Undertavle',
                              ].includes(option);
                              return (
                                <li
                                  {...props}
                                  key={option}
                                  style={{
                                    ...props.style,
                                    opacity: isDisabled ? 0.4 : 1,
                                    pointerEvents: isDisabled ? 'none' : 'auto',
                                    cursor: isDisabled
                                      ? 'not-allowed'
                                      : 'pointer',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      backgroundColor: getTypeColor(option),
                                      position: 'relative', // Changed from 'absolute' to maintain layout
                                      borderRadius: '25px',
                                      width: '28px',
                                      height: '28px',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      border: `1px solid divider`,
                                      marginRight: 1,
                                    }}
                                  >
                                    {' '}
                                    <i
                                      style={{
                                        color: 'white',
                                      }}
                                      className={`fa-solid ${getTypeIcon(option)}`}
                                    ></i>
                                  </Box>
                                  {option}
                                </li>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('buttonLabel.select_seu')}
                                inputRef={seuInputRef}
                                autoFocus={false}
                                variant="standard"
                                sx={{
                                  '& .MuiInputBase-root': {
                                    borderRadius: '10px',
                                  },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                  startAdornment: seuToAnalyse ? (
                                    <InputAdornment position="start">
                                      <Avatar
                                        variant="round"
                                        size="small"
                                        sx={{
                                          backgroundColor:
                                            getTypeColor(seuToAnalyse) ||
                                            '#555',
                                          color: '#fff',
                                          height: 32,
                                          width: 32,
                                          fontSize: 16,
                                        }}
                                      >
                                        <i
                                          className={`fas ${getTypeIcon(seuToAnalyse)}`}
                                        ></i>
                                      </Avatar>
                                    </InputAdornment>
                                  ) : null,
                                }}
                              />
                            )}
                          />

                          <Box
                            mt={1}
                            display="flex"
                            justifyContent="flex-start"
                          >
                            <Button
                              variant="text"
                              onClick={() => {
                                setAnalysisMode('node');
                              }}
                            >
                              {t('buttonLabel.select_measurement_point')}
                            </Button>
                          </Box>
                        </>
                      )}
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => parent && setNodeToAnalyse(parent)}
                        disabled={!parent}
                        sx={{
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                        }}
                      >
                        <ArrowUpwardRoundedIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  {analysisMode === 'node' && (
                    <List
                      dense
                      sx={{
                        maxHeight: 425, // or whatever height fits your layout
                        overflowY: 'auto',
                      }}
                    >
                      {children.length > 0 ? (
                        children.map((child) => (
                          <ListItemButton
                            key={child.energyGroupId}
                            onClick={() => setNodeToAnalyse(child)}
                            sx={{ borderRadius: 2 }}
                          >
                            <ListItemIcon>
                              <Avatar
                                variant="round"
                                size="small"
                                sx={{
                                  backgroundColor:
                                    getTypeColor(child.type) || '#555',
                                  color: '#fff',
                                  height: 32,
                                  width: 32,
                                  fontSize: 16,
                                }}
                              >
                                <i
                                  className={`fas ${getTypeIcon(child.type)}`}
                                ></i>
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={child.name}
                              secondary={child.type}
                            />
                          </ListItemButton>
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, textAlign: 'center', color: 'gray' }}
                        >
                          {t('power_load_profiles.no_further_breakdown')}
                        </Typography>
                      )}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default PowerLoadProfilesAnalysisTab;

const Cards = ({ data }) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  return (
    <>
      <Grid item xs={12} md={4}>
        <Card
          variant="outlined"
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <HeaderDisplay
              label={t('power_load_profiles.base')}
              description={t('power_load_profiles.tooltips.base_description')}
            />
            <ValueDisplay
              label={t('common.mean')}
              value={data?.BaseLoadSummary?.Average.toFixed(0)}
              unit="kWh"
              trend={data?.BaseLoadSummary?.TrendPercentage}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card
          variant="outlined"
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <HeaderDisplay
              label={t('power_load_profiles.intermediate')}
              description={t(
                'power_load_profiles.tooltips.intermediate_description',
              )}
            />
            <ValueDisplay
              label={t('common.mean')}
              value={data?.IntermediateLoadSummary?.Average.toFixed(0)}
              unit="kWh"
              trend={data?.IntermediateLoadSummary?.TrendPercentage}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card
          variant="outlined"
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <CardContent sx={{ flexGrow: 1 }}>
            <HeaderDisplay
              label={t('power_load_profiles.peak')}
              description={t('power_load_profiles.tooltips.peak_description')}
            />
            <ValueDisplay
              label={t('common.mean')}
              value={data?.PeakLoadSummary?.Average.toFixed(0)}
              unit="kWh"
              trend={data?.PeakLoadSummary?.TrendPercentage}
            />
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};
